import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Shield, Search, ExternalLink, Globe2, GitCompare, X } from "lucide-react";
import PublicSubmissionForm from "@/components/PublicSubmissionForm";
import { useCountryFilter } from "@/lib/useCountryFilter";
import CountryFilterBanner from "@/components/CountryFilterBanner";

const TIER_LABEL = { bronze: "Bronze", silver: "Silver", gold: "Gold", platinum: "Platinum", verified: "Verified" };
const COMPARE_STORAGE_KEY = "sp_compare_orgs";

// Curated list of top-level cause chips (subset of full taxonomy shown as pills).
const CAUSE_CHIPS = [
  "All", "Children", "Education", "Healthcare", "Emergency Response", "Refugees",
  "Poverty", "Clean Water", "Housing", "Homelessness", "Human Rights",
  "Women's Rights", "Climate Change", "Environment", "Wildlife Conservation",
  "Ocean Conservation", "Food Security", "Mental Health", "Disability",
  "Cancer", "Medical Research", "Faith-based Services", "Peace & Justice",
  "Microfinance", "Community Development", "Veterans", "Youth", "Elderly",
];

// Small tile that shows the org logo — falls back to the acronym monogram if
// the image fails to load (blocked domains / offline / broken CDN).
function OrgLogo({ org, size = 56 }) {
  const [failed, setFailed] = useState(false);
  const src = org.logo_url || org.image_url;
  const monogram = (org.acronym || org.name || "?").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "?";
  const style = { width: size, height: size };
  if (!src || failed) {
    return (
      <div className="rounded-xl grid place-items-center shrink-0 font-mono font-bold" style={{ ...style, background: "var(--wofy-surface)", color: "var(--wofy-primary)", fontSize: size / 3.2 }}>
        {monogram}
      </div>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden shrink-0 border grid place-items-center" style={{ ...style, borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
      <img src={src} alt={org.name} className="max-w-full max-h-full object-contain" onError={() => setFailed(true)} />
    </div>
  );
}

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cause, setCause] = useState("All");
  const [query, setQuery] = useState("");
  const [reach, setReach] = useState("all");
  const [tab, setTab] = useState("browse");
  const [compareKeys, setCompareKeys] = useState(() => {
    try {
      const saved = window.localStorage.getItem(COMPARE_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const navigate = useNavigate();

  useEffect(() => {
    try { window.localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(compareKeys)); }
    catch { /* ignore */ }
  }, [compareKeys]);

  const toggleCompare = (org) => {
    const key = org.slug || org.id;
    setCompareKeys((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= 4) return prev; // cap at 4
      return [...prev, key];
    });
  };

  const clearCompare = () => setCompareKeys([]);
  const goCompare = () => {
    if (compareKeys.length < 2) return;
    navigate(`/organizations/compare?ids=${compareKeys.join(",")}`);
  };

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (cause && cause !== "All") params.cause = cause;
    if (query.trim()) params.q = query.trim();
    if (reach && reach !== "all") params.reach = reach;
    const t = setTimeout(() => {
      http.get("/organizations", { params })
        .then((r) => setOrgs(r.data))
        .finally(() => setLoading(false));
    }, query ? 250 : 0); // Small debounce on typing.
    return () => clearTimeout(t);
  }, [cause, query, reach]);

  const filtered = useCountryFilter(orgs, ["country", "city", "hq_country"]);

  const reachOptions = useMemo(() => {
    const s = new Set();
    orgs.forEach((o) => { if (o.reach) s.add(o.reach); });
    return ["all", ...Array.from(s).sort()];
  }, [orgs]);

  return (
    <div data-testid="organizations-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">ORGANIZATIONS</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-3xl">A directory of the world's most trusted humanitarian & non-profit organizations.</h1>
      <p className="mt-4 max-w-2xl text-base" style={{ color: "var(--wofy-text-soft)" }}>
        Browse verified global charities, local grassroots partners, and social-impact organizations. Every entry links to the official donation and volunteer channels of the organization.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        <button data-testid="org-tab-browse" onClick={() => setTab("browse")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${tab === "browse" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "browse" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Browse organizations</button>
        <button data-testid="org-tab-submit" onClick={() => setTab("submit")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${tab === "submit" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "submit" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Submit Your Organization</button>
      </div>

      {tab === "browse" ? (
        <>
          {/* Search + filters */}
          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
              <input
                data-testid="org-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, acronym, mission, city…"
                className="w-full pl-10 pr-3 py-2.5 rounded-full border bg-transparent text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: "var(--wofy-border)" }}
              />
            </div>
            <select
              data-testid="org-reach"
              value={reach}
              onChange={(e) => setReach(e.target.value)}
              className="rounded-full px-4 py-2.5 border text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              {reachOptions.map((r) => (
                <option key={r} value={r}>{r === "all" ? "All reach" : r}</option>
              ))}
            </select>
          </div>

          {/* Cause chips */}
          <div className="mt-5 flex gap-2 flex-wrap">
            {CAUSE_CHIPS.map((c) => (
              <button
                key={c}
                data-testid={`org-cause-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                onClick={() => setCause(c)}
                className={`rounded-full px-3.5 py-1.5 text-xs border transition-colors ${cause === c ? "bg-[color:var(--wofy-text)] text-[color:var(--wofy-bg)] border-transparent" : "hover:bg-[color:var(--wofy-surface)]"}`}
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {c}
              </button>
            ))}
          </div>

          <CountryFilterBanner shown={filtered.length} total={orgs.length} noun="organizations" />
          <div className="mt-4 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            {loading ? "Loading…" : `Showing ${filtered.length} organization${filtered.length === 1 ? "" : "s"}`}
          </div>

          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((o) => {
              const key = o.slug || o.id;
              const isSelected = compareKeys.includes(key);
              const capReached = compareKeys.length >= 4 && !isSelected;
              return (
              <div
                key={o.id}
                data-testid={`org-${o.slug || o.id}`}
                className="wofy-card !p-0 overflow-hidden group flex flex-col relative"
                style={isSelected ? { boxShadow: "0 0 0 2px var(--wofy-primary)" } : {}}
              >
                {/* Compare checkbox — top right */}
                <label
                  className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-mono uppercase tracking-wider cursor-pointer transition-colors"
                  style={{
                    background: isSelected ? "var(--wofy-primary)" : "var(--wofy-bg)",
                    color: isSelected ? "#FDFBF7" : "var(--wofy-text-soft)",
                    border: "1px solid " + (isSelected ? "var(--wofy-primary)" : "var(--wofy-border)"),
                    opacity: capReached ? 0.4 : 1,
                    pointerEvents: capReached ? "none" : "auto",
                  }}
                  title={capReached ? "Compare up to 4 charities at a time" : (isSelected ? "Remove from comparison" : "Add to comparison")}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={capReached}
                    onChange={() => toggleCompare(o)}
                    data-testid={`org-compare-${o.slug || o.id}`}
                    className="w-3 h-3 accent-current"
                  />
                  Compare
                </label>
                {/* Header — logo + name (name links directly to the official site when available) */}
                <div className="p-6 flex items-start gap-4 pt-8">
                  <OrgLogo org={o} size={56} />
                  <div className="min-w-0 flex-1">
                    {o.website_url ? (
                      <a
                        href={o.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`org-title-${o.slug || o.id}`}
                        className="font-display text-lg leading-tight line-clamp-2 inline-flex items-start gap-1 hover:underline decoration-2 underline-offset-2"
                        style={{ color: "var(--wofy-text)", textDecorationColor: "var(--wofy-primary)" }}
                        title={`Visit ${o.name}'s official website`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>{o.name}</span>
                        <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 opacity-60 group-hover:opacity-100" />
                      </a>
                    ) : (
                      <div className="font-display text-lg leading-tight line-clamp-2">{o.name}</div>
                    )}
                    {o.acronym && o.acronym !== o.name && (
                      <div className="text-[11px] font-mono uppercase tracking-wider mt-1 opacity-60">{o.acronym}{o.year_founded ? ` · since ${o.year_founded}` : ""}</div>
                    )}
                  </div>
                </div>

                {/* Tagline */}
                <div className="px-6 pb-4 flex-1">
                  <p className="text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{o.tagline || o.mission}</p>
                </div>

                {/* Causes */}
                {o.causes?.length > 0 && (
                  <div className="px-6 pb-4 flex flex-wrap gap-1.5">
                    {o.causes.slice(0, 3).map((c) => (
                      <span key={c} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>{c}</span>
                    ))}
                    {o.causes.length > 3 && <span className="text-[10px] font-mono opacity-50">+{o.causes.length - 3}</span>}
                  </div>
                )}

                {/* Footer — profile link + location + tier */}
                <div className="mt-auto border-t px-6 py-3 flex items-center justify-between gap-3 text-[11px] font-mono" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
                  <span className="inline-flex items-center gap-1.5 truncate">
                    <Globe2 className="w-3 h-3 shrink-0" />
                    <span className="truncate">{o.hq_city || o.city ? `${o.hq_city || o.city}, ` : ""}{o.hq_country || o.country || o.reach || "Global"}</span>
                  </span>
                  <Link
                    to={`/organizations/${o.slug || o.id}`}
                    data-testid={`org-profile-${o.slug || o.id}`}
                    className="inline-flex items-center gap-1 hover:opacity-100 uppercase tracking-wider whitespace-nowrap"
                    style={{ color: "var(--wofy-primary)" }}
                  >
                    Profile →
                  </Link>
                  {o.verification_tier && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <Shield className="w-3 h-3" style={{ color: "var(--wofy-primary)" }} />
                      {TIER_LABEL[o.verification_tier] || o.verification_tier}
                    </span>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {!loading && filtered.length === 0 && (
            <div className="mt-10 wofy-card text-center py-10">
              <div className="font-display text-xl">No organizations match your filters.</div>
              <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>Try broadening your search or clearing the country filter.</p>
              <button data-testid="reset-filters" onClick={() => { setCause("All"); setQuery(""); setReach("all"); }} className="wofy-btn-ghost mt-4">Reset filters</button>
            </div>
          )}

          <div className="mt-14 text-[11px] font-mono uppercase tracking-wider opacity-60 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Organization names and donation links open the official website in a new tab.
          </div>
        </>
      ) : (
        <div className="mt-10 max-w-3xl">
          <PublicSubmissionForm type="organization" />
        </div>
      )}

      {/* Floating "Compare (N)" action bar — appears when 1+ orgs are selected */}
      {compareKeys.length > 0 && (
        <div
          data-testid="compare-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full shadow-xl border flex items-center gap-3 pl-5 pr-2 py-2 animate-in slide-in-from-bottom-4 duration-300"
          style={{ background: "var(--wofy-text)", color: "#FDFBF7", borderColor: "var(--wofy-text)" }}
        >
          <GitCompare className="w-4 h-4" />
          <span className="text-sm font-mono uppercase tracking-wider">
            {compareKeys.length} selected {compareKeys.length < 2 && <span className="opacity-70">· pick one more</span>}
          </span>
          <button
            data-testid="compare-go"
            onClick={goCompare}
            disabled={compareKeys.length < 2}
            className="rounded-full px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}
          >
            Compare →
          </button>
          <button
            data-testid="compare-clear"
            onClick={clearCompare}
            className="rounded-full w-8 h-8 inline-flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label="Clear selection"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
